import React, { useState, useEffect, useContext, useMemo } from 'react'
import { strCol, dateCol } from '@integec/grid-tools/lib/cols'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'
import { getApiTargetInfo } from 'core'
import { connect } from 'react-redux'
import {
  getWindUnitAccess,
  postWindUnitAccess,
  deleteWindUnitAccess,
  getWindSubgroup,
  getWindUtilityOperatorInfo,
} from 'api/pgads.js'
import { getCompanyNameMap } from 'core/selectors'
import CommonScreen from 'admin/component/CommonScreen'
import { Select, Button, DatePicker } from 'antd'
import R from 'ramda'
import styled from 'styled-components'
import { ErrorContext } from 'context'

const TableTD = styled.td`
  text-align: end;
`

const windInactiveHeaders = [
  strCol({ ident: 'companyId', display: 'Company', isKey: true, width: 300 }),
  strCol({ ident: 'utility', display: 'Utility', isKey: true, width: 400 }),
  strCol({ ident: 'subgroupId', display: 'Subgroup', isKey: true, width: 200 }),

  dateCol({
    ident: 'effectiveFrom',
    display: 'Effective From',
    isKey: true,
    width: 200,
  }),
  strCol({
    ident: 'accessType',
    display: 'Access',
    width: 200,
    // eslint-disable-next-line no-useless-computed-key
    ...toSelectionColProps({ A: 'ALL', ['R']: 'READ', N: 'NONE' }),
  }),

  modifierCol,
  modifiedDateCol,
]
const retrieveTask = apiTargetInfo => getWindUnitAccess(apiTargetInfo)

const modifyTask = apiTargetInfo => data =>
  postWindUnitAccess(apiTargetInfo)(data)

const deleteTask = apiTargetInfo => data =>
  deleteWindUnitAccess(apiTargetInfo)(data)

const title = 'Wind Subgroup Access Right'

// eslint-disable-next-line no-unused-vars
const WindInactive = ({ apiTargetInfo, companyNameMap, ...props }) => {
  const [insertEvent, setInsertEvent] = useState(false)
  const [subgroupNameMap, setSubgroupMap] = useState({})
  const [utilityMap, setUtilityMap] = useState({})
  const [utilitySubgroupMap, setUtilitySubgroupMap] = useState({})
  const { setError } = useContext(ErrorContext)
  useEffect(
    _ => {
      getWindSubgroup(apiTargetInfo).fork(setError, data => {
        const mapData = R.compose(
          R.reduce((a, b) => ({ ...a, ...b }), {}),
          R.map(d => ({ [d.subgroupId]: d.subgroupName }))
        )(data)
        setSubgroupMap(mapData)

        let utSub = R.groupBy(R.prop('utility'))(data)
        utSub = Object.keys(utSub).map(k => ({
          [k]: utSub[k].map(s => s.subgroupId),
        }))
        utSub = R.reduce((a, b) => ({ ...a, ...b }), {}, utSub)
        setUtilitySubgroupMap(utSub)
      })
      getWindUtilityOperatorInfo(apiTargetInfo).fork(setError, data => {
        const mapData = R.compose(
          R.reduce((a, b) => ({ ...a, ...b }), {}),
          R.map(d => ({ [d.utility]: d.utilityName }))
        )(data)
        setUtilityMap(mapData)
      })
    },
    [apiTargetInfo, setError]
  )

  const headerWithFormatter = windInactiveHeaders.map(header =>
    header.ident === 'subgroupId'
      ? {
          ...header,
          dataFormatter: ({ value }) =>
            subgroupNameMap[value]
              ? value + '-' + subgroupNameMap[value]
              : value,
          sortIndexGetter: ({ rowData }) =>
            subgroupNameMap[rowData.subgroupId] || rowData.subgroupId,
        }
      : header.ident === 'companyId'
      ? {
          ...header,
          dataFormatter: ({ value }) =>
            companyNameMap[value] ? value + '-' + companyNameMap[value] : value,
          sortIndexGetter: ({ rowData }) =>
            companyNameMap[rowData.companyId] || rowData.companyId,
        }
      : header.ident === 'utility'
      ? {
          ...header,
          dataFormatter: ({ value }) =>
            utilityMap[value] ? value + '-' + utilityMap[value] : value,
          sortIndexGetter: ({ rowData }) =>
            utilityMap[rowData.utility] || rowData.utility,
        }
      : header
  )

  const sortComNameList = useMemo(
    _ =>
      R.compose(
        // R.fromPairs,
        R.sortWith([R.ascend(R.prop('v'))]),
        R.map(([k, v]) => ({ k, v })),
        R.toPairs
      )(companyNameMap),
    [companyNameMap]
  )

  const insertComp = ({ insertData, setInsertData, onAdd, onCancel }) => {
    return (
      <table
        style={{
          width: '100%',
          borderSpacing: '5px',
          borderCollapse: 'inherit',
        }}
      >
        <tbody>
          <tr>
            <TableTD>Company:</TableTD>
            <td>
              <Select
                style={{ minWidth: '200px', width: '100%' }}
                onChange={value =>
                  setInsertData({ ...insertData, companyId: value })
                }
                value={insertData.companyId}
              >
                {sortComNameList.map(({ k, v }) => (
                  <Select.Option key={k} value={k}>
                    {v}
                  </Select.Option>
                ))}
              </Select>
            </td>
            <TableTD>Utility:</TableTD>
            <td>
              <Select
                style={{ minWidth: '150px', width: '100%' }}
                onChange={value =>
                  setInsertData({
                    ...insertData,
                    utility: value,
                    subgroupId: undefined,
                  })
                }
                value={insertData.utility}
              >
                {Object.keys(utilityMap).map(k => (
                  <Select.Option key={k} value={k}>
                    {utilityMap[k]}
                  </Select.Option>
                ))}
              </Select>
            </td>

            <TableTD>Subgroup:</TableTD>
            <td>
              <Select
                style={{ minWidth: '150px', width: '100%' }}
                onChange={value =>
                  setInsertData({ ...insertData, subgroupId: value })
                }
                value={insertData.subgroupId}
              >
                {(utilitySubgroupMap[insertData.utility] || []).map(k => (
                  <Select.Option key={k} value={k}>
                    {subgroupNameMap[k]}
                  </Select.Option>
                ))}
              </Select>
            </td>
          </tr>
          <tr>
            <TableTD>Access:</TableTD>
            <Select
              style={{ minWidth: '50px', width: '100%' }}
              value={insertData.accessType}
              onChange={val =>
                setInsertData({ ...insertData, accessType: val })
              }
            >
              <Select.Option value="A">ALL</Select.Option>
              <Select.Option value="R">READ</Select.Option>
              <Select.Option value="N">NONE</Select.Option>
            </Select>
            <TableTD>Effective Date:</TableTD>
            <td>
              <DatePicker
                style={{ minWidth: '150px', width: '100%' }}
                format="MM/DD/YYYY"
                value={insertData.selectedDate}
                onChange={d => {
                  setInsertData({
                    ...insertData,
                    selectedDate: d,
                    effectiveFrom: d.format('YYYY-MM-DD'),
                  })
                }}
              />
            </td>
            <td />
            <td>
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                <Button type="primary" onClick={() => onAdd(true)}>
                  Add
                </Button>
                <Button type="primary" onClick={() => onAdd(false)}>
                  Add & Hide
                </Button>
              </div>
            </td>
            <td>
              <Button type="danger" onClick={onCancel}>
                Cancel
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    )
  }

  return (
    <CommonScreen
      title={title}
      header={headerWithFormatter}
      saveTask={modifyTask}
      retrieveTask={retrieveTask}
      deleteTask={deleteTask}
      altBy={d => d.utility}
      autoRetrieve
      widthOffset={150}
      insertComp={insertEvent && insertComp}
      insertCompHeight={90}
      onInsertEvent={b => setInsertEvent(b)}
      // onRetrieveData={reloadUtilityNameMap}
      sortEnabled
      autoFixColByKey={false}
      // sortOnData={sortOnData}
      // sortOptions={[
      //   strCol({
      //     ident: 'plantIdName',
      //     sortOrder: 'asc',
      //   }),
      //   strCol({
      //     ident: 'utility',
      //     sortOrder: 'asc',
      //   }),
      // ]}
    />
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  companyNameMap: getCompanyNameMap(state),
  // utilityNameMap: getUtilityNameMap(state),
})
// eslint-disable-next-line no-unused-vars
const mapDispatchToMap = dispatch => ({
  // saveUtility: utilities =>
  //   dispatch({ type: STORE_UTILITY, payload: utilities }),
})

export default connect(mapStateToProps, mapDispatchToMap)(WindInactive)
